import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { RM_COHORTS, type RmCohort, type RMRecord } from './shared/q3';
import { authedFetch, type AuthUser } from './auth';
import {
  computeForce,
  computeForceSummary,
  computeStandings,
  computeSummary,
  computeTeams,
  matchByName,
} from './q3/standings';
import { useQuarterClock } from './q3/useQuarterClock';
import { COH } from './q3/parts';
import { initials } from './q3/format';
import HomeView from './q3/HomeView';
import MyProgressView, { type Person } from './q3/MyProgressView';
import ArenaView from './q3/ArenaView';
import ForceArenaView from './q3/ForceArenaView';
import ListView from './q3/ListView';
import { Link, RouterProvider, to, useRouter, type View } from './q3/router';
import './q3/q3.css';

const isRmView = (v: View): v is RmCohort => (RM_COHORTS as string[]).includes(v);

/**
 * The viewer's own pick from "Find yourself", per signed-in email. '' means
 * they pressed "Not you?" and we should stop auto-matching by name.
 */
const meKey = (email: string) => `shakti-q3-me:${email}`;
function readPick(email: string): string | null {
  try {
    return localStorage.getItem(meKey(email));
  } catch {
    return null;
  }
}
function writePick(email: string, empId: string): void {
  try {
    localStorage.setItem(meKey(email), empId);
  } catch {
    /* private mode: the pick just won't persist */
  }
}

interface AppProps {
  user: AuthUser;
  onSignOut: () => void;
}

export default function App({ user, onSignOut }: AppProps) {
  const [records, setRecords] = useState<RMRecord[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const { route, navigate, scroll, clearScroll } = useRouter();
  const view: View = route.view;
  /** On My Progress: someone looked up. On a board: the row to highlight. */
  const lookId = route.lookId;
  const [pick, setPick] = useState<string | null>(() => readPick(user.email));
  const [entering, setEntering] = useState(true);
  const clock = useQuarterClock();

  useEffect(() => {
    authedFetch('/api/leaderboard')
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((d: RMRecord[]) => {
        if (!Array.isArray(d) || d.length === 0) throw new Error('Empty response');
        setRecords(d);
      })
      .catch((e: Error) => setLoadError(e.message));
  }, []);

  const rows = useMemo(() => (records ? computeStandings(records) : []), [records]);
  const summary = useMemo(() => computeSummary(rows), [rows]);
  const teams = useMemo(() => computeTeams(rows), [rows]);
  const force = useMemo(() => (records ? computeForce(records) : []), [records]);
  const forceSummary = useMemo(() => computeForceSummary(force), [force]);

  // RMs and Star Force members are matched together: anyone signing in can be on either board.
  const people = useMemo((): Person[] => [...rows, ...force], [rows, force]);
  const me = useMemo((): Person | null => {
    if (pick) return people.find((r) => r.empId === pick) ?? null;
    if (pick === '') return null;
    return matchByName(people, user.name);
  }, [people, pick, user.name]);
  const shown = (view === 'me' && lookId && people.find((r) => r.empId === lookId)) || me;
  const focusId = view !== 'me' ? lookId : null;

  // Replays the entrance animation (bars growing, podium rising) on each view change.
  useEffect(() => {
    setEntering(true);
    const t = setTimeout(() => setEntering(false), 1600);
    return () => clearTimeout(t);
  }, [view]);

  // After a navigation renders: jump to the requested board row, or restore the
  // scroll position (top for new pages, the old spot for back / forward).
  // Layout effect: runs right after the new page is in the DOM, before paint, so
  // there's no flash of the old scroll position.
  // On first load (e.g. a shared /super-stars?rm=… link) jump straight to the row:
  // a smooth scroll that starts while the page is still settling gets cancelled.
  const firstScroll = useRef(true);
  useLayoutEffect(() => {
    if (!records || !scroll) return;
    const row = 'rowId' in scroll && document.querySelector(`.sk-tbl tr[data-emp="${CSS.escape(scroll.rowId)}"]`);
    if (row) row.scrollIntoView({ behavior: firstScroll.current ? 'instant' : 'smooth', block: 'center' });
    else window.scrollTo({ top: 'y' in scroll ? scroll.y : 0, behavior: 'instant' });
    firstScroll.current = false;
    clearScroll();
  }, [scroll, records, clearScroll]);

  const choose = (empId: string) => {
    writePick(user.email, empId);
    setPick(empId);
    if (lookId) navigate(to('me'));
  };

  const lookUp = (empId: string | null) => navigate(to('me', empId && empId !== me?.empId ? empId : null));

  if (loadError) {
    return (
      <div className="sk" data-view="home">
        <div className="sk-wrap">
          <div className="sk-fail">Could not load leaderboard data: {loadError}</div>
        </div>
      </div>
    );
  }

  const shownName = user.name || me?.rm || '';
  return (
    <RouterProvider navigate={navigate}>
    <div className={'sk' + (entering ? ' enter go' : '')} id="shakti-q3" data-view={view}>
      <div className="sk-wrap">
        <div className="sk-top" id="top">
          {/* The logos always lead home; on home itself it scrolls back to the top. */}
          <Link className="sk-brandrow" to={to('home')} aria-label="Shakti leaderboard home">
            <div className="sk-filogo" role="img" aria-label="FundsIndia Private Wealth" />
            <div className="sk-vr" />
            <div className="sk-toplogo sk-logo" role="img" aria-label="Shakti · Powering to 25K" />
          </Link>
          <div className="sk-topacts">
            <Link className="sk-user" to={to('me')} aria-label="My progress">
              <span className="av">{shownName ? initials(shownName) : '·'}</span>
              <span className="nm">
                {shownName || 'Find yourself'}
                <small>
                  {me ? (me.ranked ? `#${me.rank} · ` : '') + COH[me.cohort].name : 'Tap to pick your name'}
                </small>
              </span>
            </Link>
            <button className="sk-signout" onClick={onSignOut} title={`Sign out · ${user.email}`} aria-label="Sign out">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            </button>
          </div>
        </div>

        {!records ? (
          <div className="sk-loading">Loading leaderboard…</div>
        ) : (
          <>
            <HomeView
              rows={rows}
              summary={summary}
              teams={teams}
              force={force}
              forceSummary={forceSummary}
              clock={clock}
              active={view === 'home'}
            />
            {view === 'me' && (
              <MyProgressView
                rows={rows}
                force={force}
                me={me}
                shown={shown}
                pace={clock.pace}
                onPick={choose}
                onLookUp={lookUp}
                onNotMe={() => choose('')}
                onSeeOnBoard={(p) => navigate(to(p.cohort, p.empId))}
              />
            )}
            {view === 'list' && (
              <ListView
                rows={rows}
                pace={clock.pace}
                cat={route.list?.cat ?? ''}
                cohort={route.list?.cohort ?? null}
                meId={me?.empId ?? null}
              />
            )}
            {view === 'Star Force' && (
              <ForceArenaView
                force={force}
                summary={forceSummary}
                meId={me?.empId ?? null}
                focusId={focusId}
                pace={clock.pace}
              />
            )}
            {isRmView(view) && (
              <ArenaView
                key={view}
                cohort={view}
                rows={rows}
                summary={summary}
                meId={me?.empId ?? null}
                focusId={focusId}
                pace={clock.pace}
              />
            )}
          </>
        )}
      </div>
    </div>
    </RouterProvider>
  );
}
